import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { StorageAccessTier, StorageRehydratePriority, StorageResourceRef } from '../../../../../types/azureStorage';
import { ArchiveConfirm, TIERS, TierChoices } from './TierChoices';
import { Button } from '../../../../../components/ui/button';
import { ReasonButton } from '../../../../../components/ui/ReasonButton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { reprobeStorageAccountInfo } from '../../../../database-objects/hooks/useStorageAccountInfoSync';
import { useStorageAccountInfoStore } from '../../../../database-objects/store/storageAccountInfoStore';
import { STORAGE_READ_ONLY_REASON } from '../../../../../core/engines/azureStorageRefusal';
import { setStorageBlobAccessTier, toStorageOperationError } from '../../services/azureStorageApi';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { storageBlobPropertiesQueryKey } from '../../services/storageListingKeys';
import { useSingleStorageRequest } from '../hooks/useSingleStorageRequest';
import { useStorageProperties } from '../hooks/useStorageProperties';
import { QueuedDialogNotice } from './QueuedDialogNotice';
import { useReportStorageDialogGuard, type StorageDialogGuard } from './storageDialogStore';

type BlobRef = Extract<StorageResourceRef, { kind: 'blob' }>;
export type StorageTierDialogSource = 'row-menu' | 'panel' | 'chip';
/** A6: without the current tier the choices mean nothing — they stay hidden until it is read. */
export const TIER_READ_FAILED = 'The current tier could not be read; retry first.';

export function ChangeTierDialog(props: { connectionId: string; resource: BlobRef; source: StorageTierDialogSource; readOnly?: boolean; onAcknowledged?: () => void; onClose: () => void; onGuardChange?: (guard: StorageDialogGuard) => void }) {
  const queryClient = useQueryClient();
  const properties = useStorageProperties({ connectionId: props.connectionId, resource: props.resource, enabled: true });
  const entry = useStorageAccountInfoStore((state) => state.byConnection[props.connectionId]);
  const archiveCapability = entry?.status === 'ready' ? entry.info.capabilities?.['tier-archive'] : undefined;
  const current = String(properties.data?.properties.accessTier ?? properties.data?.properties.tier ?? '');
  const [tier, setTier] = useState<StorageAccessTier | ''>('');
  const [priority, setPriority] = useState<StorageRehydratePriority>('Standard');
  const [error, setError] = useState<string>();
  const [rereadFailed, setRereadFailed] = useState(false);
  // U5: Apply on Archive first asks, in the dialog, with what Archive means.
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const reload = () => { trackStorageEvent('feature:storage_blob_action', { action: 'tier_reload' }); setRereadFailed(false); setTier(''); properties.reload(); };
  const request = useSingleStorageRequest();
  useReportStorageDialogGuard(props.onGuardChange, !!tier && tier !== current, request.pending);
  useEffect(() => {
    trackStorageEvent('feature:storage_tier_dialog_open', { source: props.source });
  }, [props.source]);

  const close = () => { request.cancel(); props.onClose(); };
  // R8 (C1): why Apply is unavailable, reachable by keyboard focus; loading and a request in flight stay native disabled.
  const applyReason = props.readOnly ? STORAGE_READ_ONLY_REASON
    : !properties.data && properties.error ? TIER_READ_FAILED
    : !properties.data ? undefined
    : !tier ? 'Choose a tier first.'
    : tier === current ? `The blob is already in the ${current} tier.` : undefined;
  const answerArchive = (confirmed: boolean) => {
    trackStorageEvent('feature:storage_blob_action', { action: 'archive_confirm', result: confirmed ? 'confirmed' : 'cancelled' });
    setConfirmingArchive(false);
    if (confirmed) void save(true);
  };
  const save = async (archiveConfirmed = false) => {
    if (!tier || tier === current || props.readOnly || request.pending || !properties.data) return;
    if (tier === 'Archive' && !archiveConfirmed) { setConfirmingArchive(true); return; }
    setError(undefined);
    const outcome = await request.run((requestId) => setStorageBlobAccessTier({
      connectionId: props.connectionId, resource: props.resource, change: {
        tier, ...(current === 'Archive' && tier !== 'Archive' ? { rehydratePriority: priority } : {}),
      }, requestId, timeoutMs: 30_000,
    }));
    if (!outcome.current) return;
    const result = outcome.value;
    if (outcome.error === undefined && result) {
      trackStorageEvent('feature:storage_blob_action', { action: current === 'Archive' && tier !== 'Archive' ? 'rehydrate' : 'tier_change' });
      // The write landed either way (Fable F10): report it; a failed re-read keeps the dialog open with Reload.
      props.onAcknowledged?.();
      void queryClient.invalidateQueries({ queryKey: storageBlobPropertiesQueryKey(props.connectionId, props.resource), exact: true });
      if (!result.reread.ok) setRereadFailed(true);
      else props.onClose();
      return;
    }
    const typed = toStorageOperationError(outcome.error);
    setError(typed.message);
    if (typed.category === 'unsupported') {
      const generation = entry?.generation ?? 0;
      void reprobeStorageAccountInfo(props.connectionId, generation);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-md" data-testid="storage-tier-dialog">
        <DialogHeader><DialogTitle>Change access tier</DialogTitle><DialogDescription>Choose the storage tier of blob <span className="font-medium text-foreground [overflow-wrap:anywhere]" data-testid="storage-dialog-target">{props.resource.container}/{props.resource.name}</span>.</DialogDescription></DialogHeader>
        <QueuedDialogNotice />
        {properties.error && !properties.data ? <div className="space-y-2 text-sm" role="alert" data-testid="storage-tier-read-error">
          <p className="text-destructive">The blob's current tier could not be read: {properties.error.message}</p>
          <Button type="button" size="xs" variant="outline" onClick={reload} data-testid="storage-tier-retry">Retry</Button>
        </div> : properties.isLoading ? <p className="text-sm text-muted-foreground">Loading current tier…</p> : confirmingArchive ? <ArchiveConfirm blobName={props.resource.name} /> : <div className="space-y-2">
          <TierChoices value={tier} onChange={setTier} choices={TIERS.map((option) => {
            const unsupported = option === 'Archive' && (archiveCapability?.state === 'unsupported' || archiveCapability?.state === 'unavailable') ? archiveCapability.reason : undefined;
            return { tier: option, current: option === current, blockedReason: unsupported ?? (props.readOnly ? STORAGE_READ_ONLY_REASON : undefined) };
          })} />
          {current === 'Archive' && tier && tier !== 'Archive' && <label className="block text-sm">Rehydrate priority
            <select aria-label="Rehydrate priority" className="mt-1 h-8 w-full rounded border border-input bg-background px-2" value={priority} onChange={(event) => setPriority(event.target.value as StorageRehydratePriority)}>
              <option value="Standard">Standard</option><option value="High">High</option>
            </select>
          </label>}
          {props.readOnly && <p className="text-xs text-muted-foreground">{STORAGE_READ_ONLY_REASON}</p>}
          {error && <p className="text-xs text-destructive">{error}</p>}
          {rereadFailed && <p className="text-xs text-destructive">Saved · could not re-read <Button type="button" size="xs" variant="ghost" data-testid="storage-tier-reload" onClick={reload}>Reload</Button></p>}
        </div>}
        {confirmingArchive ? <DialogFooter>
          <Button type="button" variant="outline" onClick={() => answerArchive(false)} data-testid="storage-tier-archive-back">Keep the current tier</Button>
          <Button type="button" variant="destructive" disabled={request.pending} onClick={() => answerArchive(true)} data-testid="storage-tier-archive-confirm-button">Archive it</Button>
        </DialogFooter> : <DialogFooter><Button type="button" variant="outline" aria-label="Close" onClick={close}>Cancel</Button><ReasonButton type="button" reason={applyReason} disabled={request.pending || !properties.data} onClick={() => void save()} data-testid="storage-tier-apply">Apply tier</ReasonButton></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
